import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { ArtifactAppView } from "~/pages/manifold-nextgen/components/conversations/artifacts/apps/ArtifactAppView";
import { PublishAppButton } from "~/pages/manifold-nextgen/components/conversations/artifacts/apps/PublishAppButton";
import { useArtifactPreviewById } from "~/pages/manifold-nextgen/components/conversations/artifacts/useArtifactPreview";
import {
  type SessionArtifactApp,
  useSessionTurnAgentId,
} from "~/pages/manifold-nextgen/components/conversations/useSessionArtifactApps";
import { useSessionContextPanel } from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";

interface SessionContextArtifactAppDetailProps {
  projectId: string;
  // Managers can publish the app to the project.
  isOwner: boolean;
  artifactId: string;
  // The session's other apps, offered in the header's switcher.
  apps: SessionArtifactApp[];
  onSelect: (artifactId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
  // Exiting full screen must also reveal the panel if the user had collapsed it.
  onExitFullScreen: () => void;
}

/**
 * The header for an agent-authored HTML artifact shown as an app in the side
 * panel, with a toggle that lets it take over the session area while the chat
 * floats. The app itself renders in `SessionContextArtifactAppFrames`.
 */
export const SessionContextArtifactAppDetail = ({
  projectId,
  isOwner,
  artifactId,
  apps,
  onSelect,
  onBack,
  onCollapse,
  onExitFullScreen,
}: SessionContextArtifactAppDetailProps) => {
  const { conversationId, isAppExpanded } = useSessionContextPanel();
  const turnAgentId = useSessionTurnAgentId();
  const { preview } = useArtifactPreviewById({ conversationId, shareToken: null }, artifactId);
  const title = preview?.artifact_name ?? preview?.filename ?? "App";
  // The open app may not be in the session list yet (its reference is still
  // resolving), so it is always offered as the current view.
  const views = apps.map((app) => ({ value: app.artifactId, label: app.title }));
  if (!apps.some((app) => app.artifactId === artifactId)) {
    views.push({ value: artifactId, label: title });
  }

  return (
    <SessionContextDetailHeader
      entityName="Apps"
      onBack={onBack}
      onCollapse={isAppExpanded ? undefined : onCollapse}
      trailing={
        <>
          {isOwner && conversationId && turnAgentId && preview?.artifact_name && (
            <PublishAppButton
              projectId={projectId}
              conversationId={conversationId}
              agentId={turnAgentId}
              appName={preview.artifact_name}
            />
          )}
          <AppFullScreenToggle onExit={onExitFullScreen} />
        </>
      }
      views={views}
      currentView={artifactId}
      onSetView={onSelect}
    />
  );
};

/**
 * Every artifact app opened in the session, kept mounted and hidden while not
 * showing, so leaving an app and coming back doesn't reload it and lose its state.
 */
export const SessionContextArtifactAppFrames = ({
  projectUuid,
  artifactIds,
  activeId,
}: {
  projectUuid: string;
  artifactIds: string[];
  // null while the panel shows anything other than an artifact app.
  activeId: string | null;
}) => (
  <div className={cn("relative flex-1 overflow-hidden", activeId === null && "hidden")}>
    {artifactIds.map((id) => (
      <div key={id} className={cn("absolute inset-0 p-2", id !== activeId && "hidden")}>
        <ArtifactAppFrame projectUuid={projectUuid} artifactId={id} />
      </div>
    ))}
  </div>
);

const ArtifactAppFrame = ({
  projectUuid,
  artifactId,
}: {
  projectUuid: string;
  artifactId: string;
}) => {
  const { conversationId } = useSessionContextPanel();
  const { preview, isLoading, notFound } = useArtifactPreviewById(
    { conversationId, shareToken: null },
    artifactId
  );

  return preview ? (
    <ArtifactAppView
      preview={preview}
      projectId={projectUuid}
      conversationId={conversationId}
      className="h-full"
    />
  ) : (
    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
      {isLoading ? "Loading app…" : notFound ? "This app is no longer available." : null}
    </div>
  );
};

/** Header button that moves an app panel in and out of full screen. */
export const AppFullScreenToggle = ({ onExit }: { onExit: () => void }) => {
  const { isAppExpanded, setAppExpanded } = useSessionContextPanel();
  const label = isAppExpanded ? "Exit full screen" : "Full screen";

  return (
    <Tooltip delayDuration={700}>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => (isAppExpanded ? onExit() : setAppExpanded(true))}
          aria-label={label}
          className="size-8"
        >
          {isAppExpanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
};
